import { styled } from '@mui/material/styles'
import Box from '@/components/common/Box'
import {
	colors,
	typePresets,
	spacing,
	sizing,
	radius,
	focusVisible,
	transitions,
} from '@/styles/designTokens'

// Continuous #fafafa band wrapping the breakdown rows and the Amount Due toggle.
export const AccordionRoot = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	backgroundColor: colors.surfaceMuted,
	flexShrink: 0,
})

// Expanded container: pt-24 px-24 pb-0 — no bottom padding, divider provides
// the visual bottom edge. gap-24 separates the rows block from the divider.
// Figma node 672:21741 "Expanded".
export const BreakdownSection = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	gap: spacing.xl,
	padding: `${spacing.xl}px ${spacing.xl}px 0`,
	flexShrink: 0,
	// Emotion's display:flex overrides the UA stylesheet's display:none for [hidden].
	// Re-apply expected behaviour so aria-controls can reference an always-mounted element.
	'&[hidden]': { display: 'none' },
})

// Inner wrapper for the individual rows — gap-4 between each row.
// Figma node 672:21742 "Breakdown".
export const BreakdownRows = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	gap: spacing.xs,
})

export const BreakdownRow = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: spacing.sm,
})

export const BreakdownLabel = styled('span')({
	...typePresets.bodySm,
	color: colors.textPrimary,
})

export const BreakdownValue = styled('span')({
	...typePresets.bodySm,
	color: colors.textPrimary,
	flexShrink: 0,
	fontVariantNumeric: 'tabular-nums',
})

// Divider at the bottom of BreakdownSection, separating it from the Amount Due toggle.
export const BreakdownDivider = styled('hr')({
	margin: 0,
	border: 'none',
	borderTop: `1px solid ${colors.divider}`,
})

// Amount Due toggle button — py-12 px-24. No borderTop: the BreakdownDivider above
// provides the visual separation when expanded; the AccordionRoot borderTop
// (set by the parent OrderSummaryFooter / CartMutedSection) handles the collapsed state.
export const AmountDueButton = styled('button')({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: spacing.sm,
	padding: `${spacing.sm}px ${spacing.xl}px`,
	backgroundColor: 'transparent',
	border: 'none',
	width: '100%',
	cursor: 'pointer',
	fontFamily: 'inherit',
	textAlign: 'left',
	flexShrink: 0,
	'&:focus-visible': {
		outline: focusVisible.outline,
		outlineColor: colors.focusBorder,
		outlineOffset: focusVisible.outlineOffset,
	},
	'&:disabled': {
		cursor: 'default',
	},
})

export const AmountDueChevronWrap = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded }) => ({
	width: sizing.navIcon,
	height: sizing.navIcon,
	borderRadius: radius.circle,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	color: colors.textSecondary,
	transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
	transition: `transform ${transitions.spatial}`,
}))

// sx object for the ExpandMoreIcon — keeps visual sizing out of .tsx
export const chevronIconSx = { fontSize: sizing.navIcon } as const

export const AmountDueLabel = styled('span')({
	...typePresets.headingLg,
	flex: '1 1 auto',
	minWidth: 0,
	color: colors.textPrimary,
})

export const AmountDueValue = styled('span')({
	...typePresets.headingLg,
	color: colors.textPrimary,
	flexShrink: 0,
	fontVariantNumeric: 'tabular-nums',
})
